// Bernie
import { ILocalization } from 'bernie-l10n';
import { merge } from 'bernie-core';

import { IRoomQueryAdapter, queryObject } from './adapters';

const { withoutKeys } = merge;

const rooms: IRoomQueryAdapter = {
  parseInRoomParams: (query: any): any => {
    const MAX_ROOM_PARAMS = 10;
    const roomParams = [];
    const roomsCollection = [];
    const adultsRegex = /a(\d+)/i;
    const childrenRegex = /c(\d+)/i;

    /*
     sample query object:

     {
       rm1: 'a2:c4:c0:c5',
       rm2: 'a1',
       foo: 'bar'
     }
     */

    // extract valid rooms from the query object
    for (let index = 1, key = '', value = ''; index <= MAX_ROOM_PARAMS; index++) {
      key = `rm${index}`;

      if (query.hasOwnProperty(key)) {
        value = query[key];

        if (typeof value === 'string' && value.length > 0) {
          roomParams.push(query[key]);

          delete query[key];
        }

        continue;
      }

      break;
    }

    // cycle through the rooms and parseIn their content
    roomParams.forEach(room => {
      if (adultsRegex.test(room)) {
        const adults = parseInt(adultsRegex.exec(room)[1], 10) || 0;
        const children = [];
        const roomParts = room.split(':');

        roomParts.forEach(part => {
          if (childrenRegex.test(part)) {
            children.push({
              age: parseInt(childrenRegex.exec(part)[1], 10) || 0,
            });
          }
        });

        if (adults) {
          roomsCollection.push({
            adults,
            children,
          });
        }
      }
    });

    query.rooms = roomsCollection;

    return query;
  },

  parseInGuestParams: (query: any): any => {
    /*
    sample guest query object:
      {
        adults: '2,1',
        children: '3,0',
        childAge: '4,0,5'
      }
    */

    const roomsCollection = [];
    const adultsArray = getParameterArray(query, 'adults');
    const childrenArray = getParameterArray(query, 'children');
    const childAgeArray = getParameterArray(query, 'childAge');

    adultsArray.forEach((adult, i) => {
      const adults = parseInt(adult, 10) || 0;
      const children = [];
      const childCount = (childrenArray && childrenArray[i] && parseInt(childrenArray[i], 10)) || 0;
      const childAges = (childAgeArray && childAgeArray.splice(0, childCount)) || [];

      childAges.forEach(childAge => {
        children.push({
          age: parseInt(childAge, 10) || 0,
        });
      });

      if (adults) {
        roomsCollection.push({
          adults,
          children,
        });
      }
    });

    query.rooms = roomsCollection;

    delete query.adults;
    delete query.children;
    delete query.childAge;

    return query;
  },

  parseIn: (query: any): any => {
    query = queryObject(query);

    const hasRoomParams = query && query.rm1 !== undefined;

    if (hasRoomParams) {
      return parseInRoomParams(query);
    }

    return parseInGuestParams(query);
  },

  parseOut: (query: any): any => {
    query = queryObject(query);

    const roomsCollection = query && query.rooms;
    let count = 1;

    if (typeof roomsCollection === 'object' && roomsCollection && roomsCollection.length > 0) {
      roomsCollection.forEach(room => {
        if (room.adults > 0) {
          const roomStrings = [`a${room.adults}`];

          if (room.children && room.children.length > 0) {
            room.children.forEach(child => {
              roomStrings.push(`c${child.age || 0}`);
            });
          }

          query[`rm${count}`] = roomStrings.join(':');
          count += 1;
        }
      });

      delete query.rooms;
    }

    return query;
  },
};

function parseIn(query: any): any {
  const startDate = query.chkin || query.startDate || null;
  const endDate = query.chkout || query.endDate || null;

  const dateFields = {};

  if (startDate) {
    // @ts-ignore
    dateFields.startDate = startDate;
  }

  if (endDate) {
    // @ts-ignore
    dateFields.endDate = endDate;
  }

  return withoutKeys(['chkin', 'chkout']).merge({}, rooms.parseIn(query), dateFields);
}

function parseOut(query: any, l10n: ILocalization): any {
  if (!query) {
    return {};
  }

  const chkin = parseDate(query.startDate, query.localDateFormat, l10n);
  const chkout = parseDate(query.endDate, query.localDateFormat, l10n);

  const dates: any = {};

  if (chkin) {
    dates.chkin = chkin;
  }

  if (chkout) {
    dates.chkout = chkout;
  }

  return withoutKeys(['startDate', 'endDate', 'localDateFormat', 'top_dp']).merge({}, rooms.parseOut(query, l10n), dates);
}

function parseInRoomParams(query: any): any {
  return rooms.parseInRoomParams(query);
}

function parseInGuestParams(query: any): any {
  return rooms.parseInGuestParams(query);
}

function parseDate(date: string = '', outputFormat: string = 'yyyy-MM-dd', l10n: ILocalization): string {
  try {
    const parsedDate = l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;

    if (parsedDate) {
      return l10n.formatDate(parsedDate, { raw: outputFormat });
    }
  } catch (error) {
    return date;
  }
}

function getParameterArray(query: any, parameter: string): string[] {
  const paramterValue = query && query[parameter];

  return (paramterValue && typeof paramterValue === 'string' && paramterValue.split(',')) || [];
}

export const InfositeQueryAdapter: IRoomQueryAdapter = {
  parseIn,
  parseOut,
  parseInRoomParams,
  parseInGuestParams,
};
